/**
 * Dashboard filter state — reads/writes URL search params so filters
 * survive refresh and are bookmarkable.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DashboardFilters, DashboardObservation } from './types';

/** Default date range: last 6 months from today. */
function defaultFrom(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d.toISOString().slice(0, 10);
}

function defaultTo(): string {
  return new Date().toISOString().slice(0, 10);
}

export function useDashboardFilters() {
  const [params, setParams] = useSearchParams();

  const filters: DashboardFilters = useMemo(() => ({
    from: params.get('from') || defaultFrom(),
    to: params.get('to') || defaultTo(),
    sites: params.get('sites') ? params.get('sites')!.split(',') : [],
    observer: params.get('observer') || '',
    minScore: params.get('minScore') ? Number(params.get('minScore')) : null,
  }), [params]);

  const setFilters = useCallback(
    (patch: Partial<DashboardFilters>) => {
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        if (patch.from !== undefined) {
          if (patch.from) next.set('from', patch.from);
          else next.delete('from');
        }
        if (patch.to !== undefined) {
          if (patch.to) next.set('to', patch.to);
          else next.delete('to');
        }
        if (patch.sites !== undefined) {
          if (patch.sites.length > 0) next.set('sites', patch.sites.join(','));
          else next.delete('sites');
        }
        if (patch.observer !== undefined) {
          if (patch.observer) next.set('observer', patch.observer);
          else next.delete('observer');
        }
        if (patch.minScore !== undefined) {
          if (patch.minScore !== null) next.set('minScore', String(patch.minScore));
          else next.delete('minScore');
        }
        return next;
      }, { replace: true });
    },
    [setParams],
  );

  return { filters, setFilters };
}

/** Apply filters to an observations array (AND-composed). */
export function applyFilters(
  observations: DashboardObservation[],
  filters: DashboardFilters,
): DashboardObservation[] {
  return observations.filter((o) => {
    if (filters.from && o.observationDate < filters.from) return false;
    if (filters.to && o.observationDate > filters.to) return false;
    if (filters.sites.length > 0 && !filters.sites.includes(o.siteName)) return false;
    if (filters.observer && o.observerName !== filters.observer) return false;
    if (filters.minScore !== null && (o.totalAverage === null || o.totalAverage < filters.minScore)) return false;
    return true;
  });
}
